import React, { useState, useEffect, useRef, useContext } from "react";
import "./AddStockPage.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import Input from "../../Components/Input/Input";
import DropDown from "../../Components/DropDown/DropDown";
import AuthContext from "../../context/Services/Auth";
import Message from "../../Components/Message/Message";
import ProductImageGallery from "../../Components/ProductImageGallery/ProductImageGallery";
import { useTranslation } from "react-i18next";

import { LuPackagePlus } from "react-icons/lu";
import { FaRegSave } from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import { TbFileDescription } from "react-icons/tb";
import { MdDriveFileRenameOutline, MdOutlinePriceCheck } from "react-icons/md";
import { IoMdArrowDropdown } from "react-icons/io";
import { BiCategory } from "react-icons/bi";
import { AiOutlineTag } from "react-icons/ai";
import { FaImages } from "react-icons/fa";

import {
  create_stock,
  get_product,
  update_stock,
} from "../../context/Services/Stock";
import { get_all_categories } from "../../context/Services/Settings";

const AddStockPage = () => {
  const { t } = useTranslation();
  const { userData } = useContext(AuthContext);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const boxRef = useRef(null);

  const initial = {
    product_name: "",
    quantity: "",
    product_note: "",
    stock_details: [],
    buy_price: 0,
    prix_de_vente: 0,
    remise: 0,
    type: "",
    categories: [],
    sell_price: 0,
    comission: 0,
    sub_category: null,
    created: new Date(),
  };

  const [product, setProduct] = useState(initial);
  const [categories, setCategories] = useState([]);

  const initial_error = {
    product_name: null,
    quantity: null,
    product_note: null,
    stock_details: null,
    buy_price: null,
    prix_de_vente: null,
    remise: null,
    type: null,
    sell_price: null,
    comission: null,
    categories: null,
    sub_category: null,
  };

  const [errors, setErrors] = useState(initial_error);
  const [principalImage, setPrincipalImage] = useState(null);

  const [loading, setLoading] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  const get_form_data = () => {
    const formData = new FormData();
    let errs = {};

    for (const key in product) {
      if (Array.isArray(product[key])) {
        formData.append(key, JSON.stringify(product[key]));
      } else if (product[key] instanceof Date) {
        formData.append(key, product[key].toISOString());
      } else {
        if (
          (product[key] === "" || product[key] === null) &&
          key !== "product_note" &&
          key !== "quantity" &&
          key !== "image_path" &&
          key !== "prix_de_vente" &&
          key !== "remise" &&
          key !== "type" &&
          key !== "categories" &&
          key !== "sub_category"
        ) {
          errs[key] = t("Vous devez remplir toutes les informations");
        } else {
          formData.append(key, product[key] ?? "");
        }
      }
    }

    return formData;
  };

  const create__stock = () => {
    let payload = get_form_data();
    if (!payload) {
      return;
    }

    setLoading(true);
    create_stock(payload)
      .then((res) => {
        setTimeout(() => {
          setLoading(false);
          setProduct(initial);
        }, 1500);
      })
      .catch((e) => setLoading(false));
  };

  const update__stock = () => {
    setLoading(true);

    let payload = get_form_data();

    if (!payload) {
      setLoading(false);
      return;
    }

    update_stock(id, payload)
      .then((res) => {
        setTimeout(() => {
          setLoading(false);
          setProduct(res.data);
        }, 1500);
      })
      .catch((e) => setLoading(false));
  };

  const handle_save = () => {
    if (id) {
      update__stock();
    } else {
      create__stock();
    }
  };

  const handle_cancel = () => {
    if (id) {
      setReadOnly(true);
    } else {
      navigate("/stock");
    }
  };

  const handle_principal_change = (image) => {
    setPrincipalImage(image?.image_path || null);
  };

  const handle_get_categories = () => {
    get_all_categories()
      .then((res) => {
        setCategories(res?.data || []);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  useEffect(() => {
    if (userData?.role === "commercial") {
      return navigate("/");
    }

    handle_get_categories();

    if (id && location?.search) {
      if (location.search?.split("=")[1] === "readonly") {
        setReadOnly(true);
      }
    }

    if (id) {
      get_product(id)
        .then((res) => {
          let json = res?.data;

          setProduct({
            product_name: json?.product_name,
            quantity: json?.quantity,
            product_note: json?.product_note,
            buy_price: json?.buy_price,
            prix_de_vente: json?.prix_de_vente || 0,
            remise: json?.remise || 0,
            type: json?.type || "",
            categories: json?.categories?.map(c => c._id || c) || [],
            created: json?.created,
          });

          // Set principal image from API response
          setPrincipalImage(json?.principal_image || null);
        })
        .catch((e) => console.log(e));
    }
  }, [id, location]);

  return (
    <>
      <Message loading={loading} status={{ status: loading }} />

      <div className="clients_overview latest_clients_overview shadow">
        <div className="chart_section_header flex items-center gap">
          <LuPackagePlus style={{ fontSize: "1.5rem" }} />
          <b>{t("Add New Stock")}</b>

          <div className="relative to-right flex gap">
            {!readOnly && (
              <div
                className="flex gap items-center filter_button"
                onClick={handle_cancel}
              >
                <RxCross2 style={{ color: "red", fontSize: "1.2rem" }} />
                <b>{t("Cancel")}</b>
              </div>
            )}

            {readOnly ? (
              <div
                className="flex gap items-center filter_button"
                onClick={() => setReadOnly(false)}
              >
                <MdDriveFileRenameOutline
                  style={{ color: "#7983ff", fontSize: "1.2rem" }}
                />
                <b>{t("Edit")}</b>
              </div>
            ) : (
              <div
                className="flex gap items-center filter_button"
                onClick={handle_save}
              >
                <FaRegSave style={{ color: "#7983ff", fontSize: "1.2rem" }} />
                <b>{t("Save")}</b>
              </div>
            )}
          </div>
        </div>
        <div className="main_info flex gap">
          <div className="general_info">
            <b>{t("General information")}</b>

            <div className="form_container flex flex-col gap relative">
              <div className="flex flex-col" style={{ gap: "10px" }}>
                <h5>{t("Product Name")}</h5>
                <Input
                  icon={<MdDriveFileRenameOutline className="icon_m" />}
                  placeholder={t("Product Name")}
                  setChange={(e) => {
                    setProduct((prev) => ({ ...prev, product_name: e }));
                  }}
                  value={product.product_name}
                  readOnly={readOnly}
                />
                {errors?.product_name && (
                  <h6 style={{ color: "red" }}>{errors?.product_name}</h6>
                )}
              </div>

              <div className="splitter"></div>

              <div className="flex flex-col" style={{ gap: "10px" }}>
                <h5>{t("Type")}</h5>
                <Input
                  icon={<AiOutlineTag className="icon_m" />}
                  placeholder={t("Type de produit")}
                  setChange={(e) =>
                    setProduct((prev) => ({ ...prev, type: e }))
                  }
                  value={product.type}
                  readOnly={readOnly}
                />
              </div>

              <div className="flex flex-col" style={{ gap: "10px" }}>
                <h5>{t("Catégories")}</h5>
                <div className="relative full">
                  <DropDown
                    fullWidth={true}
                    customButtonStyle="justify-start category_dropdown"
                    dropdownStyle="dropup"
                    readOnly={readOnly}
                    ignoreDropDown={false}
                    button={
                      <>
                        <BiCategory />
                        <b>
                          {product.categories?.length > 0
                            ? `${product.categories.length} catégorie(s) sélectionnée(s)`
                            : t("Sélectionner des catégories")}
                        </b>
                        <IoMdArrowDropdown className="to-right" />
                      </>
                    }
                  >
                    {categories.map((cat) => {
                      const isSelected = product.categories?.includes(cat._id);
                      return (
                        <div
                          key={cat._id}
                          className={`drop_item flex items-center gap ${isSelected ? 'selected' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setProduct((prev) => {
                              const currentCategories = prev.categories || [];
                              if (currentCategories.includes(cat._id)) {
                                return { ...prev, categories: currentCategories.filter(id => id !== cat._id) };
                              } else {
                                return { ...prev, categories: [...currentCategories, cat._id] };
                              }
                            });
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => { }}
                            style={{ marginRight: "8px" }}
                          />
                          {cat.value}
                        </div>
                      );
                    })}
                  </DropDown>
                </div>
              </div>

              <div className="flex flex-col" style={{ gap: "10px" }}>
                <h5>Quantity</h5>
                <Input
                  placeholder="Quantity"
                  setChange={(e) =>
                    setProduct((prev) => ({ ...prev, quantity: e }))
                  }
                  value={product.quantity}
                  readOnly={readOnly}
                  type="number"
                />
              </div>
              <div className="flex flex-col" style={{ gap: "10px" }}>
                <h5>{t("Prix d'achat")}</h5>
                <Input
                  icon={<MdOutlinePriceCheck className="icon_m" />}
                  placeholder={t("Product d'achat")}
                  setChange={(e) =>
                    setProduct((prev) => ({ ...prev, buy_price: e }))
                  }
                  value={product.buy_price}
                  readOnly={readOnly}
                />
              </div>

              {/* Prix de Vente - Admin only */}
              {userData?.role === "admin" && (
                <div className="flex flex-col" style={{ gap: "10px" }}>
                  <h5>{t("Prix de vente")} <span style={{ fontSize: "12px", color: "#888" }}>({t("minimum")})</span></h5>
                  <Input
                    icon={<MdOutlinePriceCheck className="icon_m" />}
                    placeholder={t("Prix de vente minimum")}
                    setChange={(e) =>
                      setProduct((prev) => ({ ...prev, prix_de_vente: e }))
                    }
                    value={product.prix_de_vente}
                    readOnly={readOnly}
                    type="number"
                  />
                  <p style={{ fontSize: "12px", color: "#888", marginTop: "-5px" }}>
                    {t("Les commerciaux ne pourront pas vendre en dessous de ce prix")}
                  </p>
                </div>
              )}

              {/* Remise - Admin only */}
              {userData?.role === "admin" && (
                <div className="flex flex-col" style={{ gap: "10px" }}>
                  <h5>{t("Remise")} <span style={{ fontSize: "12px", color: "#888" }}>(%)</span></h5>
                  <Input
                    icon={<MdOutlinePriceCheck className="icon_m" />}
                    placeholder={t("Remise en pourcentage")}
                    setChange={(e) =>
                      setProduct((prev) => ({ ...prev, remise: e }))
                    }
                    value={product.remise}
                    readOnly={readOnly}
                    type="number"
                  />
                  <p style={{ fontSize: "12px", color: "#888", marginTop: "-5px" }}>
                    {t("Pourcentage de réduction appliqué sur le prix dans les bons de livraison")}
                  </p>
                </div>
              )}

              <div className="flex flex-col" style={{ gap: "10px" }}>
                <h5>{t("Product Note")}</h5>
                <Input
                  icon={<TbFileDescription className="icon_m" />}
                  placeholder={"Note"}
                  setChange={(e) =>
                    setProduct((prev) => ({ ...prev, product_note: e }))
                  }
                  value={product.product_note}
                  readOnly={readOnly}
                />
                {errors?.product_note && (
                  <h6 style={{ color: "red" }}>{errors?.product_note}</h6>
                )}
              </div>
            </div>
          </div>
          <div className="image_info">
            <b>{t("Image Principale")}</b>
            <div className="image_container flex justify-center items-center">
              {principalImage ? (
                <img src={principalImage} className="full_product_img" alt="Principal" onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <div className="flex flex-col items-center gap" style={{ color: "#888" }}>
                  <FaImages style={{ fontSize: "2rem" }} />
                  <span style={{ fontSize: "12px", textAlign: "center" }}>
                    {id ? t("Sélectionnez une image principale dans la galerie ci-dessous") : t("Ajoutez des images après avoir créé le produit")}
                  </span>
                </div>
              )}
            </div>

            {/* Product Image Gallery - only for existing products */}
            {id && (
              <div style={{ marginTop: "20px" }}>
                <ProductImageGallery
                  stockId={id}
                  readOnly={readOnly}
                  onPrincipalChange={handle_principal_change}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AddStockPage;
